'use client';

// ─────────────────────────────────────────────────────────────────────────────
// FRONTMATTER FIELDS CONSUMED BY GUIDE PAGES (set these in your guide's MDX):
//
// Identity & SEO:
//   title, description, h1, subHeadline, urlPath, publishedTime, modifiedTime,
//   author, keywords, category, priority
//
// Hero CTA:
//   heroCtaText, heroCtaLink, heroCtaSubtext
//
// Guide-specific (drives hero pills + HowTo schema):
//   guideConfig.totalSections          (display only)
//   guideConfig.estimatedReadingTime   (display + Article.timeRequired schema)
//   guideConfig.difficulty             (display + Article.educationalLevel)
//   guideConfig.chapterTitles          (HowTo schema steps)
//     ⚠ MUST match the H2 order in the MDX body. Sequential #heading-N anchors
//       are derived at runtime from live H2s. If you reorder body H2s without
//       updating chapterTitles, HowTo step URLs point to the wrong sections.
//       Not enforced — manual discipline required.
//
// LLM optimization (drives Article schema for AI search):
//   llmOptimization.primaryEntity      (Article.about.name)
//   llmOptimization.entityAttributes   (Article.about.description)
//   llmOptimization.questionsAnswered  (Article.teaches)
//     If absent, GuideSchema falls back to the hardcoded "Mortgage Lead
//     Generation" topic — wrong for non-lead-gen guides. Always declare.
//
// FAQ schema (auto-emits FAQPage JSON-LD via GuideSchema):
//   faqs: [{ question, answer, schema: true }, ...]
//
// Related content panel:
//   relatedPages: [{ href, anchor, title }, ...]
//     ⚠ Must be an array of OBJECTS, not strings. A plain string like
//       "/some-path" makes <Link href={page.href}> receive undefined → crash.
//
// Author attribution (drives Person schema for E-E-A-T + AI citation):
//   author: "Andrew Pawlak"  → emits @type: Person
//   author omitted           → falls back to @type: Organization "LeadPops"
//
// FRONTMATTER FIELDS NOT CONSUMED BY GUIDE PAGES (do not bother setting):
//   schema:                  (GuideSchema emits Article + HowTo + FAQPage +
//                             BreadcrumbList directly; ignores this array)
//   readTime                 (use guideConfig.estimatedReadingTime instead)
//   sidebarPrimaryCTA,       (no template code reads these on guides)
//   sidebarSecondaryCTA
//   images.hero,             (guides don't render hero images; text-only hero)
//   images.heroMobile
//   robots                   (default behavior is index+follow; redundant)
//   socialProof              (not displayed in guide hero)
//   contentRequirements      (advisory only, no render impact)
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useEffect, useRef, useCallback } from 'react';
import { PageMatter, ProcessedContent } from '@/lib/content';
import { SocialProofBanner } from '@/components/SocialProof/SocialProofBanner';
import { FAQSection } from '@/components/Content/FAQSection';
import { RelatedContent } from '@/components/Content/RelatedContent';
import { Breadcrumbs } from '@/components/Navigation/Breadcrumbs';
import { CtaBlock } from '@/components/mdx/CtaBlock';
import { ReadingProgressBar } from '@/components/Guide/ReadingProgressBar';
import { GuideSidebar } from '@/components/Guide/GuideSidebar';
import { SectionNavigation } from '@/components/Guide/SectionNavigation';
import { Menu, Bookmark } from '@/lib/icons';
import Image from 'next/image';
import { getAuthorProfile } from '@/lib/author';
import { scrollToElement, initializeScrollPositioning } from '@/components/Guide/utils/scroll-positioning';

// Simple throttle function for performance
function throttle<T extends (...args: any[]) => any>(func: T, wait: number): T {
  let timeout: NodeJS.Timeout | null = null;
  let lastCallTime = 0;
  
  return ((...args: Parameters<T>) => {
    const now = Date.now();
    const timeSinceLastCall = now - lastCallTime;
    
    if (timeSinceLastCall >= wait) {
      lastCallTime = now;
      func(...args);
    } else if (!timeout) {
      timeout = setTimeout(() => {
        lastCallTime = Date.now();
        func(...args);
        timeout = null;
      }, wait - timeSinceLastCall);
    }
  }) as T;
}

interface GuidePageTemplateProps {
  content: ProcessedContent;
  breadcrumbs: Array<{ name: string; url: string }>;
  fullSlug: string;
}

export function GuidePageTemplate({ content, breadcrumbs, fullSlug }: GuidePageTemplateProps) {
  const { meta } = content;
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [currentSection, setCurrentSection] = useState('');
  const [scrollProgress, setScrollProgress] = useState(0);
  const [headings, setHeadings] = useState<Array<{ id: string; text: string; level: number }>>([]);
  const [cachedH2Elements, setCachedH2Elements] = useState<Element[]>([]);
  const rafIdRef = useRef<number>(0);
  const scrollPositionRef = useRef<number>(0);
  const [pendingScrollTarget, setPendingScrollTarget] = useState<string | null>(null);
  
  // Bookmark state management
  const [bookmarkPosition, setBookmarkPosition] = useState<number | null>(null);
  const [showBookmarkToast, setShowBookmarkToast] = useState(false);
  const [hasBookmark, setHasBookmark] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const bookmarkKey = `guideProgress:${fullSlug}`;
  
  // Check for bookmark on mount and route changes
  useEffect(() => {
    const checkBookmark = () => {
      // Don't restore if URL has anchor or from TOC
      const hasAnchor = window.location.hash !== '';
      const fromTOC = sessionStorage.getItem('fromTOC') === 'true';
      
      if (!hasAnchor && !fromTOC) {
        const savedPosition = localStorage.getItem(bookmarkKey);
        if (savedPosition) {
          const position = parseInt(savedPosition);
          setBookmarkPosition(position);
          setHasBookmark(true);
          setShowBookmarkToast(true);
          
          // Auto-hide toast after 8 seconds
          setTimeout(() => {
            setShowBookmarkToast(false);
          }, 8000);
        }
      }
      
      // Clear TOC flag
      sessionStorage.removeItem('fromTOC');
    };
    
    // Check on mount
    checkBookmark();
    
    // Listen for route changes (client-side navigation)
    const handleRouteChange = () => {
      // Small delay to ensure DOM is ready
      setTimeout(checkBookmark, 100);
    };
    
    // Next.js router events for client-side navigation
    window.addEventListener('popstate', handleRouteChange);
    
    return () => {
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, [bookmarkKey]);
  
  // Save bookmark position on scroll
  useEffect(() => {
    let scrollTimeout: NodeJS.Timeout;
    
    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      
      scrollTimeout = setTimeout(() => {
        const currentScrollY = window.scrollY;
        
        if (currentScrollY > 300) {
          const previousPosition = localStorage.getItem(bookmarkKey);
          const currentPosStr = Math.round(currentScrollY).toString();
          
          if (!previousPosition || Math.abs(parseInt(previousPosition) - currentScrollY) > 100) {
            localStorage.setItem(bookmarkKey, currentPosStr);
            setHasBookmark(true);
            setIsSaving(true);
            setTimeout(() => setIsSaving(false), 2000);
          }
        }
      }, 1000);
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      clearTimeout(scrollTimeout);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [bookmarkKey]);
  
  // Extract headings on mount and initialize scroll positioning
  useEffect(() => {
    const extractHeadings = () => {
      const contentElement = document.querySelector('.guide-content');
      if (!contentElement) return;
      
      const headingElements = contentElement.querySelectorAll('h2, h3');
      const extracted = Array.from(headingElements).map((heading, index) => ({
        id: `heading-${index}`,
        text: heading.textContent || '',
        level: parseInt(heading.tagName.charAt(1))
      }));
      
      // Add IDs to actual headings
      headingElements.forEach((heading, index) => {
        heading.id = `heading-${index}`;
      });
      
      setHeadings(extracted);
      
      // Cache H2 elements for performance
      const h2Elements = Array.from(contentElement.querySelectorAll('h2'));
      setCachedH2Elements(h2Elements);
    };
    
    // Initialize bulletproof scroll positioning
    initializeScrollPositioning();
    
    // Delay to ensure content is rendered
    setTimeout(extractHeadings, 100);
  }, [content]);
  
  // Track scroll progress and current section with performance optimizations
  useEffect(() => {
    const handleScroll = () => {
      // Calculate progress
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrolled = window.scrollY;
      const progress = scrollHeight > 0 ? (scrolled / scrollHeight) * 100 : 0;
      
      // Use requestAnimationFrame for smooth updates
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = requestAnimationFrame(() => {
        setScrollProgress(Math.min(100, Math.max(0, progress)));
      });
      
      // Find current section using cached elements
      if (cachedH2Elements.length > 0) {
        let current = '';
        
        cachedH2Elements.forEach((heading) => {
          const rect = heading.getBoundingClientRect();
          if (rect.top <= 150) {
            current = heading.textContent || '';
          }
        });
        
        setCurrentSection(current);
      }
    };
    
    // Throttle scroll handler to 60fps (16ms)
    const throttledHandleScroll = throttle(handleScroll, 16);
    
    // Add passive listener for better performance
    window.addEventListener('scroll', throttledHandleScroll, { passive: true });
    handleScroll(); // Initial call
    
    return () => {
      window.removeEventListener('scroll', throttledHandleScroll);
      cancelAnimationFrame(rafIdRef.current);
    };
  }, [cachedH2Elements]);
  
  // Handle body scroll lock for mobile modal
  useEffect(() => {
    if (isSidebarOpen) {
      // Save current scroll position
      scrollPositionRef.current = window.scrollY;
      
      // Lock body scroll (iOS-compatible method)
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollPositionRef.current}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
    } else {
      // Restore body scroll
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      
      // CRITICAL FIX: Restore scroll position immediately to prevent flash to top
      // Only restore if there's NO pending navigation target
      if (scrollPositionRef.current > 0 && !pendingScrollTarget) {
        window.scrollTo(0, scrollPositionRef.current);
        scrollPositionRef.current = 0; // Reset after restoration
      } else if (scrollPositionRef.current > 0 && pendingScrollTarget) {
        // If there's a pending target, restore position but DON'T reset the ref
        // The deferred scroll will handle navigation from this restored position
        window.scrollTo(0, scrollPositionRef.current);
      }
    }
    
    // Cleanup on unmount
    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
    };
  }, [isSidebarOpen]);
  
  // Handle deferred scrolling after modal closes
  useEffect(() => {
    if (!isSidebarOpen && pendingScrollTarget) {
      // Wait for modal animation to complete (300ms) + buffer
      const timeoutId = setTimeout(async () => {
        // RACE CONDITION FIX: Add 100ms delay to let competing operations settle
        setTimeout(async () => {
          try {
            const result = await scrollToElement({
              targetElementId: pendingScrollTarget,
              forceScrollBehavior: 'instant', // CRITICAL: Use instant for precision
              debugMode: false,
            });
          
            if (!result.success) {
              console.warn(`[GuidePageTemplate] Failed to scroll to deferred target: ${pendingScrollTarget}`);
            }
          } catch (error) {
            console.error(`[GuidePageTemplate] Error in deferred scroll:`, error);
            // Fallback to old method
            const element = document.getElementById(pendingScrollTarget);
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }
          
          setPendingScrollTarget(null);
        }, 100); // Race condition fix: let competing operations settle
      }, 320); // 300ms animation + 20ms buffer for snappier response
      
      return () => clearTimeout(timeoutId);
    }
  }, [isSidebarOpen, pendingScrollTarget]);
  
  // Bookmark handlers
  const handleContinueReading = () => {
    if (bookmarkPosition !== null) {
      window.scrollTo({
        top: bookmarkPosition,
        behavior: 'smooth'
      });
    }
    setShowBookmarkToast(false);
  };
  
  const handleStartFromTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    setShowBookmarkToast(false);
  };
  
  // Guide configuration from frontmatter
  const guideConfig = meta.guideConfig || {
    totalSections: headings.filter(h => h.level === 2).length,
    estimatedReadingTime: Math.ceil(content.wordCount / 200),
    difficulty: 'intermediate',
    downloadable: false,
    chapterTitles: headings.filter(h => h.level === 2).map(h => h.text)
  };
  
  return (
    <div className="min-h-screen bg-white">
      {/* Reading Progress Bar - Below Nav */}
      <ReadingProgressBar 
        progress={scrollProgress} 
        currentSection={currentSection}
      />
      
      {/* Breadcrumbs */}
      <div className="w-full bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <Breadcrumbs items={breadcrumbs} />
        </div>
      </div>
      
      {/* Hero Section */}
      <div className="w-full gradient-hero">
        <div className="pt-12 md:pt-16 lg:pt-20 pb-8 md:pb-10 lg:pb-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="mb-4">
              <span className="inline-block px-3 py-1 text-sm font-semibold text-primary bg-primary/10 rounded-full">
                {guideConfig.difficulty.charAt(0).toUpperCase() + guideConfig.difficulty.slice(1)} Guide
              </span>
              <span className="inline-block ml-2 px-3 py-1 text-sm font-semibold text-gray-600 bg-gray-100 rounded-full">
                {guideConfig.estimatedReadingTime} min read
              </span>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-gray-900 leading-tight">
              {meta.h1 || meta.title || 'Untitled Guide'}
            </h1>
            {meta.subHeadline && (
              <div className="mb-8 max-w-3xl mx-auto text-center">
                {/* Author headshot for the Apocalypse guide */}
                {fullSlug === 'guide/mortgage-lead-generation-apocalypse' && (
                  <div className="mb-4">
                    <Image
                      src="/Andrew-Pawlak-LeadPops-rebeliQ.jpeg"
                      alt="Andrew Pawlak - CEO and Co-Founder of LeadPops and rebeliQ, Author of The Mortgage Lead Generation Apocalypse Playbook"
                      width={80}
                      height={80}
                      className="rounded-full mx-auto border-2 border-gray-200 shadow-lg"
                      priority={true}
                      quality={90}
                    />
                  </div>
                )}
                <div className="text-lg md:text-xl text-gray-700">
                  {meta.subHeadline.split('\n').map((line, index) => (
                    <div key={index} className={index === 0 ? "font-semibold" : "text-gray-600"}>
                      {line}
                    </div>
                  ))}
                </div>
                {/* Author byline — guides are editorial content, so known
                    authors get the full linked byline (same entity the Article
                    schema references). Renders nothing for team-authored guides. */}
                {(() => {
                  const profile = getAuthorProfile(meta.author);
                  if (!profile) return null;
                  return (
                    <div className="mt-6 flex justify-center">
                      <a
                        href={profile.pagePath}
                        className="group inline-flex items-center gap-3 bg-white border border-gray-200 rounded-full pl-1.5 pr-6 py-1.5 shadow-sm hover:border-primary hover:shadow transition-all"
                      >
                        <Image
                          src={profile.image}
                          alt={profile.name}
                          width={40}
                          height={40}
                          className="rounded-full ring-2 ring-gray-100 group-hover:ring-primary/30 transition-all"
                        />
                        <span className="text-left leading-tight">
                          <span className="block text-sm text-gray-600">
                            By{' '}
                            <span className="font-semibold text-gray-900 group-hover:text-primary transition-colors">
                              {profile.name}
                            </span>
                          </span>
                          <span className="block text-xs text-gray-500 mt-0.5">
                            {profile.title}
                          </span>
                        </span>
                      </a>
                    </div>
                  );
                })()}
                <div className="mt-4">
                  <span className="inline-flex items-center gap-2 px-3 py-1 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200/50 rounded-full">
                    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M5 4a2 2 0 012-2h6a2 2 0 012 2v14l-5-2.5L5 18V4z" />
                    </svg>
                    Progress automatically saves • Press {typeof window !== 'undefined' && navigator.userAgent.toLowerCase().indexOf('mac') !== -1 ? 'Cmd' : 'Ctrl'}+D to bookmark
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Main Content Area with Sidebar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <div className="flex gap-8">
          {/* Desktop Sidebar */}
          <aside className="hidden lg:block w-80 flex-shrink-0">
            <div className="sticky top-32">
              <GuideSidebar
                headings={headings}
                scrollProgress={scrollProgress}
                estimatedReadingTime={guideConfig.estimatedReadingTime}
                currentSection={currentSection}
                guideUrl={fullSlug}
                hasBookmark={hasBookmark}
                isSaving={isSaving}
                guideTitle={meta.h1 || meta.title}
                guideSubtitle={meta.subHeadline}
                guideAuthor={meta.author}
                guideContent={content.compiledContent}
                rawContent={content.content}
              />
            </div>
          </aside>
          
          {/* Main Content */}
          <main className="flex-1 min-w-0">
            {/* Mobile Jump to Section Button */}
            <button
              onClick={() => setIsSidebarOpen(true)}
              className="lg:hidden fixed bottom-20 right-6 z-50 bg-primary text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-shadow"
              aria-label="Jump to section"
            >
              <Menu className="w-6 h-6" />
            </button>
            
            {/* Mobile Sidebar Modal */}
            <div className={`lg:hidden fixed inset-0 z-50 pointer-events-none ${isSidebarOpen ? 'pointer-events-auto' : ''}`}>
              <div 
                className={`absolute inset-0 bg-black transition-opacity duration-300 ease-in-out ${isSidebarOpen ? 'bg-opacity-50' : 'bg-opacity-0 pointer-events-none'}`} 
                onClick={() => setIsSidebarOpen(false)} 
              />
              <div className={`absolute right-0 top-0 h-full w-[90%] bg-white shadow-xl flex flex-col overflow-hidden transform transition-transform duration-300 ease-in-out ${isSidebarOpen ? 'translate-x-0' : 'translate-x-full'}`}>
                  <button
                    onClick={() => setIsSidebarOpen(false)}
                    className="absolute top-4 right-4 z-10 text-gray-500 hover:text-gray-700"
                  >
                    ✕
                  </button>
                  <GuideSidebar
                    headings={headings}
                    scrollProgress={scrollProgress}
                    estimatedReadingTime={guideConfig.estimatedReadingTime}
                    currentSection={currentSection}
                    guideUrl={fullSlug}
                    hasBookmark={hasBookmark}
                    isSaving={isSaving}
                    guideTitle={meta.h1 || meta.title}
                    guideSubtitle={meta.subHeadline}
                    guideAuthor={meta.author}
                    guideContent={content.compiledContent}
                    rawContent={content.content}
                    onItemClick={(targetId?: string) => {
                      if (targetId) {
                        setPendingScrollTarget(targetId);
                      }
                      setIsSidebarOpen(false);
                    }}
                  />
              </div>
            </div>
            
            
            {/* Guide Content */}
            <article className="guide-content prose prose-lg max-w-none prose-a:text-primary prose-a:underline hover:prose-a:text-primary-700">
              {content.compiledContent ? (
                <div>{content.compiledContent}</div>
              ) : (
                <div className="text-gray-600">
                  <p>Content is being processed. Please refresh the page.</p>
                </div>
              )}
            </article>
            
            {/* Section Navigation */}
            <SectionNavigation
              headings={headings.filter(h => h.level === 2)}
              currentSection={currentSection}
            />
            
            {/* FAQs Section */}
            {meta.faqs && meta.faqs.length > 0 && (
              <div className="mt-12">
                <FAQSection faqs={meta.faqs} />
              </div>
            )}
            
            {/* Related Content */}
            {meta.relatedPages && meta.relatedPages.length > 0 && (
              <div className="mt-12">
                <RelatedContent pages={meta.relatedPages} />
              </div>
            )}
            
            {/* CTA Section */}
            {meta.heroCtaText && meta.heroCtaLink && (
              <div className="mt-16">
                <CtaBlock
                  headline="Ready to Generate Your Own Mortgage Leads?"
                  subheadline="Stop reading about it. Start doing it with LeadPops."
                  buttonText={meta.heroCtaText}
                  buttonHref={meta.heroCtaLink}
                  buttonSubtext={meta.heroCtaSubtext}
                  background="gradient"
                  size="large"
                  icon="arrow"
                />
              </div>
            )}
          </main>
        </div>
      </div>
      
      {/* Bookmark Toast Notification - Rendered at page level */}
      {showBookmarkToast && (
        <div className="fixed bottom-24 left-1/2 transform -translate-x-1/2 z-[9999] animate-in fade-in duration-300">
          <div className="bg-white border-2 border-gray-200 px-6 py-4 rounded-lg shadow-xl max-w-md">
            <div className="flex items-center space-x-3 mb-3">
              <Bookmark className="w-5 h-5 text-[#294bfd] fill-current" />
              <p className="font-medium text-gray-900">Welcome back! Continue where you left off?</p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleContinueReading}
                className="flex-1 px-4 py-2 bg-[#294bfd] text-white font-medium rounded-md hover:bg-[#1a3acc] transition-colors"
              >
                Continue Reading
              </button>
              <button
                onClick={handleStartFromTop}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 font-medium rounded-md hover:bg-gray-200 transition-colors"
              >
                Start from Top
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
